import { CreateLeadsDto, LeadsEntity, UpdateLeadsDto } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, LeadStatus, UserRole } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { In, Repository } from 'typeorm';

@Injectable()
export class LeadsService {
  private readonly logger = new Logger(LeadsService.name);
  
  constructor(
    @InjectRepository(LeadsEntity)
    private readonly leadsRepo: Repository<LeadsEntity>,
  ) {}

  // Get all leads
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const leads = await this.leadsRepo.find({
        relations: ['created_by', 'updated_by', 'client'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.GET_ALL.MESSAGE,
        data: leads,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.LEADS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get leads for supervisor: created by managers or supervisors only
  async getForSupervisor(): Promise<ApiResponseInterface> {
    try {
      const leads = await this.leadsRepo.find({
        where: { created_by: { role: In([UserRole.MANAGER, UserRole.SUPERVISOR]) } },
        relations: ['created_by', 'updated_by', 'client'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.GET_ALL.MESSAGE,
        data: leads,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.LEADS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get leads by user ID (creator)
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const leads = await this.leadsRepo.find({
        where: { created_by: { id: userId } },
        relations: ['created_by', 'updated_by', 'client'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.GET_BY_ID.MESSAGE,
        data: leads,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.LEADS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Get lead by ID
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const lead = await this.leadsRepo.findOne({
        where: { id },
        relations: ['created_by', 'updated_by', 'client'],
      });

      if (!lead) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.LEADS.GET_BY_ID.CODE,
          message: 'Lead not found',
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.GET_BY_ID.MESSAGE,
        data: lead,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.LEADS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Create new lead
  async create(dto: CreateLeadsDto): Promise<ApiResponseInterface> {
    try {
      const newLead = this.leadsRepo.create(dto);
      await this.leadsRepo.save(newLead);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.CREATE.MESSAGE,
        data: newLead,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.CREATE.CODE,
        message: API_RESPONSE.ERROR.LEADS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // Update existing lead
  async update(dto: UpdateLeadsDto): Promise<ApiResponseInterface> {
    try {
      const lead = await this.leadsRepo.findOne({
        where: { id: dto.id },
        relations: ['updated_by', 'client'],
      });

      if (!lead) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.LEADS.UPDATE.CODE,
          message: 'Lead not found',
          data: null,
        };
      }

      const updateData = prepareUpdateFields(dto, {
        updated_by_id: 'updated_by',
        client_id: 'client',
      });

      Object.assign(lead, updateData);
      await this.leadsRepo.save(lead);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.UPDATE.MESSAGE,
        data: lead,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.LEADS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // Soft delete lead
  async softDelete(dto: UpdateLeadsDto): Promise<ApiResponseInterface> {
    try {
      const lead = await this.leadsRepo.findOne({ where: { id: dto.id } });

      if (!lead) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.LEADS.DELETE.CODE,
          message: 'Lead not found',
          data: null,
        };
      }

      lead.status = LeadStatus.DELETED;
      await this.leadsRepo.save(lead);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.LEADS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.LEADS.DELETE.MESSAGE,
        data: lead,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.LEADS.DELETE.CODE,
        message: API_RESPONSE.ERROR.LEADS.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}